import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  Stack,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Chip,
  TextField,
  Autocomplete,
} from '@mui/material';
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  rectIntersection,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InboxIcon from '@mui/icons-material/Inbox';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import { useTranslation } from 'react-i18next';

import MyButton from '../../../components/MyButton/MyButton';
import SearchBar from '../../../components/DataTable/SearchBar/SearchBar';
import { useReportManagement } from '../../../hooks/useReportManagement';
import { useNotification } from '../../../hooks/useNotification';
import { useReportFlowTags } from '../../../hooks/useReportFlowTags';
import { useReportTypes } from '../../../hooks';
import useBacklogReports from '../hooks/useBacklogReports';
import KanbanCard from './KanbanCard';
import BacklogColumn from './BacklogColumn';
import ArchiveColumn from './ArchiveColumn';
import EditFlowDialog from './EditFlowDialog';
import CompleteItemDialog from './CompleteItemDialog';
import CardDetailModal from './CardDetailModal';
import FlowTagManagerDialog from './FlowTagManagerDialog';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';

// ─── KanbanColumn ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;

const KanbanColumn = React.memo(function KanbanColumn({ column, items, onComplete, onArchive, onCardClick, isRoot, canEdit, itemTagMap, reportTypeMap }) {
  const { t } = useTranslation();
  const completedCount = items.filter((i) => !!i.completion_date).length;
  const { setNodeRef, isOver } = useDroppable({
    id: `column-${column.id}`,
    data: { columnId: column.id },
  });

  // Lazy load: render only the first `visibleCount` cards. As the sentinel
  // at the bottom enters the viewport, reveal another PAGE_SIZE chunk.
  // Keeps DOM and dnd-kit's sortable registry small even on 200+ card boards.
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);

  // Reset paging when the underlying items list changes (column refetch, etc.)
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [items.length]);

  useEffect(() => {
    if (visibleCount >= items.length) return undefined;
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisibleCount((c) => Math.min(c + PAGE_SIZE, items.length));
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visibleCount, items.length]);

  const visibleItems = useMemo(
    () => items.slice(0, visibleCount),
    [items, visibleCount],
  );
  const sortableIds = useMemo(
    () => visibleItems.map((i) => String(i.id)),
    [visibleItems],
  );
  const remaining = items.length - visibleItems.length;

  const colColor = column.column_color || '#94a3b8';
  // Resolve effective action with backward-compat to is_completion_column.
  const effectiveAction =
    column.column_action ||
    (column.is_completion_column ? 'SET_REPORT_COMPLETED' : null);

  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: 320,
        minWidth: 320,
        maxWidth: 320,
        // Board container'in tum yuksekligini al ki body asagi dogru
        // overflow'a ulasabilsin.
        height: '100%',
        bgcolor: isOver ? 'rgba(59, 130, 246, 0.04)' : '#f8fafc',
        borderRadius: '12px',
        border: '1px solid',
        borderColor: isOver ? 'primary.main' : '#e2e8f0',
        flexShrink: 0,
        transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
        boxShadow: isOver
          ? '0 0 0 2px rgba(59, 130, 246, 0.15)'
          : '0 1px 3px rgba(0,0,0,0.04)',
        overflow: 'hidden',
      }}
    >
      {/* Colored top border */}
      <Box sx={{ height: 4, bgcolor: colColor, borderRadius: '12px 12px 0 0' }} />

      {/* Column header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.75,
          py: 1.25,
          bgcolor: '#fff',
          borderBottom: '1px solid #f1f5f9',
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: '#1e293b',
          }}
        >
          {column.column_title}
        </Typography>
        {effectiveAction === 'SET_REPORT_COMPLETED' && (
          <Tooltip
            title={t('reportManagement.createFlow.actionSetCompleted', {
              defaultValue: 'Rapor durumunu tamamlandıya çevir',
            })}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: '0.95rem', color: '#16a34a' }} />
          </Tooltip>
        )}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 22,
            height: 22,
            borderRadius: '6px',
            bgcolor: '#f1f5f9',
            color: '#64748b',
            fontSize: '0.72rem',
            fontWeight: 700,
            px: 0.5,
          }}
        >
          {items.length}
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          px: 1,
          py: 1,
          // Her kolon kendi içinde dikey scroll yapar; sayfa bütünü kaymaz.
          // minHeight:0 olmazsa flex child scroll'u doğru hesaplamaz.
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          // Ince scrollbar
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: '#cbd5e1',
            borderRadius: 3,
            '&:hover': { bgcolor: '#94a3b8' },
          },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          // GPU-promote the cards container so the browser composites it on
          // its own layer; cuts re-paint cost during drag on tall boards.
          contain: 'layout style',
          willChange: 'transform',
        }}
      >
        <SortableContext
          items={sortableIds}
          strategy={verticalListSortingStrategy}
        >
          {visibleItems.map((item) => (
            <KanbanCard
              key={item.id}
              item={item}
              columnColor={colColor}
              onComplete={onComplete}
              onArchive={onArchive}
              onCardClick={onCardClick}
              isRoot={isRoot}
              canEdit={canEdit}
              flowTags={itemTagMap?.[item.id] || []}
              reportTypeMap={reportTypeMap}
            />
          ))}
        </SortableContext>

        {/* Lazy-load sentinel + remaining hint */}
        {remaining > 0 && (
          <Box ref={sentinelRef} sx={{ py: 1, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500 }}>
              +{remaining} {t('reports.board.more', { defaultValue: 'kart daha' })}
            </Typography>
          </Box>
        )}

        {/* Empty column placeholder */}
        {items.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 5,
              mx: 0.5,
              border: '2px dashed #e2e8f0',
              borderRadius: '8px',
              bgcolor: '#fafbfc',
            }}
          >
            <InboxIcon sx={{ fontSize: '1.5rem', color: '#cbd5e1', mb: 0.75 }} />
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {t('reports.board.noCards', { defaultValue: 'Kart yok' })}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Completion column footer */}
      {column.is_completion_column && items.length > 0 && (
        <Box
          sx={{
            px: 1.75,
            py: 1,
            borderTop: '1px solid #dcfce7',
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            bgcolor: '#f0fdf4',
          }}
        >
          <CheckCircleOutlineIcon sx={{ fontSize: '0.9rem', color: '#16a34a' }} />
          <Typography sx={{ fontSize: '0.75rem', color: '#166534', fontWeight: 600 }}>
            {completedCount} / {items.length} {t('reports.board.completed', { defaultValue: 'tamamlandi' })}
          </Typography>
          {/* Progress bar */}
          <Box sx={{ flex: 1, ml: 0.5, height: 4, bgcolor: '#bbf7d0', borderRadius: 2, overflow: 'hidden' }}>
            <Box
              sx={{
                width: `${items.length > 0 ? (completedCount / items.length) * 100 : 0}%`,
                height: '100%',
                bgcolor: '#16a34a',
                borderRadius: 2,
                transition: 'width 0.3s ease',
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
});

// ─── ReportBoardView ──────────────────────────────────────────────────────────

const ReportBoardView = ({
  flows,
  selectedFlowId,
  onFlowSelect,
  isRoot,
  canCreate = false,
  canEdit = false,
  onCreateFlow,
  onRefresh,
  // Notify parent (ReportManagementPage) that an item's underlying report
  // status changed so the Reports list view can refetch and stay in sync.
  onReportsChanged,
}) => {
  const { t } = useTranslation();
  const {
    fetchFlowDetail,
    moveItem,
    addItemToFlow,
    completeItem,
    archiveItem,
    unarchiveItem,
    fetchArchivedItems,
    archiveFlow,
    restoreFlow,
    deleteFlow,
  } = useReportManagement();
  const { showNotification } = useNotification();

  // Rapor turu id -> ad map. KanbanCard'daki `report.reportType` (job_reports
  // JSONB icinde ID olarak saklaniyor) ismini bu map'ten cozer — aksi halde
  // kartta "1" gibi ham ID goruluyordu.
  const { reportTypes: allReportTypes } = useReportTypes();
  const reportTypeMap = useMemo(() => {
    const map = {};
    (allReportTypes || []).forEach((rt) => {
      if (rt?.id != null) map[String(rt.id)] = rt.report_type_name;
    });
    return map;
  }, [allReportTypes]);

  // Akis-ozel etiket hook'u. selectedFlowId degistiginde otomatik yeniden fetch.
  const {
    tags: flowTags,
    itemTagMap,
    createTag: createFlowTag,
    updateTag: updateFlowTag,
    deleteTag: deleteFlowTag,
    setItemTags: setItemFlowTags,
  } = useReportFlowTags(selectedFlowId);

  const [flowDetail, setFlowDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [columnItems, setColumnItems] = useState({});
  const [activeItem, setActiveItem] = useState(null);
  const [completeDialogItem, setCompleteDialogItem] = useState(null);
  const [editFlowOpen, setEditFlowOpen] = useState(false);
  const [archivedItems, setArchivedItems] = useState([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  // ─── Flow tab / archive UI state ──────────────────────────────────────────
  const [flowTab, setFlowTab] = useState(0); // 0 = Active, 1 = Archived (ROOT only)
  // Arsivlenmis bir akis YENIDEN ACILMADAN salt-okunur goruntuleniyor
  // (Ibrahim 2026-10-26). true iken board arsiv sekmesinden acilmistir;
  // duzenleme / suruklenme / arsivden cikarma kapali.
  const [viewingArchived, setViewingArchived] = useState(false);
  const [flowSearch, setFlowSearch] = useState('');
  // ─── Board filters (client-side, only affect rendering) ───────────────────
  const [filters, setFilters] = useState({
    technical: [],
    financial: [],
    projectNumber: '',
    firm: [],
    status: [],
    team: [],
    tagIds: [], // Akis-ozel etiket filtresi (id array)
  });
  const [tagManagerOpen, setTagManagerOpen] = useState(false);
  const [flowMenuAnchor, setFlowMenuAnchor] = useState(null);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [restoreConfirmFlow, setRestoreConfirmFlow] = useState(null);
  const [deleteConfirmFlow, setDeleteConfirmFlow] = useState(null);
  const [actionInFlight, setActionInFlight] = useState(false);

  // Split active vs archived flows from the global list
  const activeFlows = useMemo(
    () => (flows || []).filter((f) => !f.is_archived),
    [flows]
  );
  const archivedFlows = useMemo(
    () => (flows || []).filter((f) => !!f.is_archived),
    [flows]
  );

  // Apply search filter (only on the visible list)
  const filteredActiveFlows = useMemo(() => {
    const q = flowSearch.trim().toLowerCase();
    if (!q) return activeFlows;
    return activeFlows.filter((f) =>
      (f.flow_title || '').toLowerCase().includes(q)
    );
  }, [activeFlows, flowSearch]);

  const filteredArchivedFlows = useMemo(() => {
    const q = flowSearch.trim().toLowerCase();
    if (!q) return archivedFlows;
    return archivedFlows.filter((f) =>
      (f.flow_title || '').toLowerCase().includes(q)
    );
  }, [archivedFlows, flowSearch]);

  // If a previously selected flow disappears (e.g. archived), clear it
  useEffect(() => {
    if (!selectedFlowId) return;
    const stillVisibleInActive = activeFlows.some(
      (f) => String(f.id) === String(selectedFlowId)
    );
    if (!stillVisibleInActive && flowTab === 0 && !viewingArchived) {
      // Move selection to first active or clear
      if (onFlowSelect) onFlowSelect(activeFlows[0]?.id || '');
    }
  }, [activeFlows, selectedFlowId, flowTab, onFlowSelect, viewingArchived]);

  // Currently selected flow object (for menu / actions)
  const selectedFlow = useMemo(
    () => (flows || []).find((f) => String(f.id) === String(selectedFlowId)),
    [flows, selectedFlowId]
  );

  const handleFlowTabChange = (_e, newVal) => {
    setFlowTab(newVal);
    setViewingArchived(false);
    // When switching to archived tab, clear board selection
    if (newVal === 1 && onFlowSelect) onFlowSelect('');
  };

  // ─── Flow archive / restore / delete handlers ─────────────────────────────
  const handleOpenFlowMenu = (e) => setFlowMenuAnchor(e.currentTarget);
  const handleCloseFlowMenu = () => setFlowMenuAnchor(null);

  const handleArchiveClick = () => {
    setFlowMenuAnchor(null);
    setArchiveConfirmOpen(true);
  };

  const handleConfirmArchive = async () => {
    if (!selectedFlow) return;
    setActionInFlight(true);
    try {
      await archiveFlow(selectedFlow.id);
      showNotification(
        t('reportManagement.flows.archiveSuccess', { defaultValue: 'Akış arşivlendi' }),
        'success'
      );
      setArchiveConfirmOpen(false);
      // Switch selection to next active flow or clear
      const remainingActive = activeFlows.filter((f) => f.id !== selectedFlow.id);
      if (onFlowSelect) onFlowSelect(remainingActive[0]?.id || '');
      if (onRefresh) onRefresh();
    } catch (_err) {
      showNotification(
        t('reportManagement.flows.archiveError', { defaultValue: 'Akış arşivlenemedi' }),
        'error'
      );
    } finally {
      setActionInFlight(false);
    }
  };

  const handleConfirmRestore = async () => {
    if (!restoreConfirmFlow) return;
    setActionInFlight(true);
    try {
      await restoreFlow(restoreConfirmFlow.id);
      showNotification(
        t('reportManagement.flows.restoreSuccess', { defaultValue: 'Akış yeniden açıldı' }),
        'success'
      );
      setRestoreConfirmFlow(null);
      if (onRefresh) onRefresh();
    } catch (_err) {
      showNotification(
        t('reportManagement.flows.restoreError', { defaultValue: 'Akış yeniden açılamadı' }),
        'error'
      );
    } finally {
      setActionInFlight(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmFlow) return;
    setActionInFlight(true);
    try {
      await deleteFlow(deleteConfirmFlow.id);
      showNotification(
        t('reportManagement.flows.deleteSuccess', { defaultValue: 'Akış silindi' }),
        'success'
      );
      setDeleteConfirmFlow(null);
      if (onRefresh) onRefresh();
    } catch (_err) {
      showNotification(
        t('reportManagement.flows.deleteError', { defaultValue: 'Akış silinemedi' }),
        'error'
      );
    } finally {
      setActionInFlight(false);
    }
  };

  // Drag aktivasyonu: distance-based (task board pattern). Card'a kisa
  // tiklama (< 8px hareket) drag baslatmaz — cift tik CardDetailModal'i acar,
  // buton/menu tiklamalari serbest kalir. Sadece 8px+ hareket edince surukle.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  // ── Load flow detail ──────────────────────────────────────────────────────

  const loadFlowDetail = useCallback(
    async (flowId) => {
      if (!flowId) {
        setFlowDetail(null);
        setColumnItems({});
        setArchivedItems([]);
        return;
      }
      setDetailLoading(true);
      setArchiveLoading(true);
      try {
        const [detail, archived] = await Promise.all([
          fetchFlowDetail(flowId),
          fetchArchivedItems(flowId),
        ]);
        setFlowDetail(detail);
        setArchivedItems(archived);
        if (detail?.report_flow_columns) {
          const grouped = {};
          for (const col of detail.report_flow_columns) {
            grouped[col.id] = col.report_flow_items || [];
          }
          setColumnItems(grouped);
        }
      } finally {
        setDetailLoading(false);
        setArchiveLoading(false);
      }
    },
    [fetchFlowDetail, fetchArchivedItems]
  );

  useEffect(() => {
    loadFlowDetail(selectedFlowId);
  }, [selectedFlowId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sorted columns ────────────────────────────────────────────────────────

  const sortedColumns = useMemo(() => {
    if (!flowDetail?.report_flow_columns) return [];
    return [...flowDetail.report_flow_columns].sort(
      (a, b) => a.column_order - b.column_order
    );
  }, [flowDetail]);

  // ── Helpers: extract user / firm display info from a flow item ────────────

  const getUserInfo = (user) => {
    if (!user) return null;
    const id = String(user.id);
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || `#${id}`;
    return { id, name };
  };

  const getFirmName = (item) =>
    item?.job?.contract?.firm?.firm_name || item?.job?.firm_short_name || '';

  // ── Backlog (must be declared BEFORE option-list memos that read it) ──────

  const existingReportIds = useMemo(() => {
    const ids = new Set();
    for (const items of Object.values(columnItems)) {
      for (const item of items) {
        if (item.report_id) ids.add(String(item.report_id));
      }
    }
    for (const item of archivedItems) {
      if (item.report_id) ids.add(String(item.report_id));
    }
    return ids;
  }, [columnItems, archivedItems]);

  const { backlogItems, loading: backlogLoading, refetch: refetchBacklog } = useBacklogReports(flowDetail, existingReportIds);

  // ── Build option lists from full columnItems + backlog ────────────────────
  // Backlog rows are included so the user can filter by responsibles / firms
  // / project numbers that only appear among Bekleyen Raporlar.

  const flatItems = useMemo(() => {
    const arr = [];
    for (const items of Object.values(columnItems)) arr.push(...items);
    return arr;
  }, [columnItems]);

  // Filter option lists are built from columns + backlog so values that
  // only exist among Bekleyen Raporlar can still be selected.
  const optionItems = useMemo(
    () => [...flatItems, ...(backlogItems || [])],
    [flatItems, backlogItems],
  );

  const technicalOptions = useMemo(() => {
    const map = new Map();
    for (const it of optionItems) {
      const u = getUserInfo(it?.job?.job_assignment?.technical_user);
      if (u && !map.has(u.id)) map.set(u.id, u);
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [optionItems]);

  const financialOptions = useMemo(() => {
    const map = new Map();
    for (const it of optionItems) {
      const u = getUserInfo(it?.job?.job_assignment?.financial_user);
      if (u && !map.has(u.id)) map.set(u.id, u);
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [optionItems]);

  const firmOptions = useMemo(() => {
    const set = new Set();
    for (const it of optionItems) {
      const f = getFirmName(it);
      if (f) set.add(f);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [optionItems]);

  const teamOptions = useMemo(() => {
    const map = new Map();
    for (const it of optionItems) {
      const team = it?.job?.job_assignment?.technical_team;
      const id = team?.id != null ? String(team.id) : null;
      const name = team?.name || null;
      if (id && name && !map.has(id)) map.set(id, { id, name });
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [optionItems]);

  const STATUS_FILTER_OPTIONS = useMemo(
    () => ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'OVERDUE'],
    [],
  );

  const hasActiveFilter =
    filters.technical.length > 0 ||
    filters.financial.length > 0 ||
    filters.firm.length > 0 ||
    filters.status.length > 0 ||
    filters.team.length > 0 ||
    filters.tagIds.length > 0 ||
    !!filters.projectNumber.trim();

  // ── Apply filters to columnItems (renders only — does NOT touch DnD source) ─

  // Single predicate so the same filter logic applies to columnItems AND
  // the backlog list — keeps both views consistent.
  // Effective status: COMPLETED durumu completion_date veya rapor status'unden
  // gelir; endDate bugünden geçmişse ve tamamlanmadıysa OVERDUE.
  const getEffectiveStatus = useCallback((it) => {
    if (it?.completion_date) return 'COMPLETED';
    const reportId = it?.report_id != null ? String(it.report_id) : null;
    const reports = Array.isArray(it?.job?.job_reports) ? it.job.job_reports : [];
    const r = reportId ? reports.find((x) => String(x?.id) === reportId) : null;
    const raw = r?.status || null;
    if (raw === 'COMPLETED') return 'COMPLETED';
    const endStr = r?.endDate;
    if (endStr) {
      const end = new Date(endStr);
      if (!Number.isNaN(end.getTime())) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        if (end < today) return 'OVERDUE';
      }
    }
    return raw || 'PENDING';
  }, []);

  const matchesFilters = useCallback((it) => {
    if (!hasActiveFilter) return true;
    const projQ = filters.projectNumber.trim().toLowerCase();
    if (filters.technical.length > 0) {
      const id = it?.job?.job_assignment?.technical_user?.id;
      if (!id || !filters.technical.includes(String(id))) return false;
    }
    if (filters.financial.length > 0) {
      const id = it?.job?.job_assignment?.financial_user?.id;
      if (!id || !filters.financial.includes(String(id))) return false;
    }
    if (filters.firm.length > 0) {
      const fn = (getFirmName(it) || '').toLowerCase();
      // Substring + case-insensitive eşleşme: free-text girişlerle de
      // çalışsın (örn. kullanıcı "designist" yazınca tam adı yakalansın).
      const matched = filters.firm.some(
        (val) => fn && fn.includes(String(val).toLowerCase())
      );
      if (!matched) return false;
    }
    if (filters.status.length > 0) {
      const s = getEffectiveStatus(it);
      if (!filters.status.includes(s)) return false;
    }
    if (filters.team.length > 0) {
      const tid = it?.job?.job_assignment?.technical_team?.id;
      if (tid == null || !filters.team.includes(String(tid))) return false;
    }
    if (projQ) {
      const pn = String(it?.job?.project_number || '').toLowerCase();
      if (!pn.includes(projQ)) return false;
    }
    if (filters.tagIds.length > 0) {
      const cardTags = itemTagMap?.[it.id] || [];
      const cardTagIds = new Set(cardTags.map((t) => t.id));
      // OR mantigi — secilen etiketlerden en az biri karta atanmisa gecer.
      if (!filters.tagIds.some((id) => cardTagIds.has(id))) return false;
    }
    return true;
  }, [filters, hasActiveFilter, getEffectiveStatus, itemTagMap]);

  // Gelecekteki raporu gizle SADECE sürekli akışta (CONTINUOUS). Dönem
  // bazlı akış (PERIOD_BASED) için dönem penceresine düşen gelecek
  // raporlar da planlama amaciyla gorunmelidir — matchFlowScope zaten
  // window kontrolunu yapiyor.
  const isFutureReport = useCallback((it, flowType) => {
    if (flowType !== 'CONTINUOUS') return false;
    const reportId = it?.report_id != null ? String(it.report_id) : null;
    if (!reportId) return false;
    const reports = Array.isArray(it?.job?.job_reports) ? it.job.job_reports : [];
    const r = reports.find((x) => String(x?.id) === reportId);
    const startStr = r?.startDate;
    if (!startStr) return false;
    const start = new Date(startStr);
    if (Number.isNaN(start.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return start > today;
  }, []);

  const filteredColumnItems = useMemo(() => {
    const result = {};
    const flowType = selectedFlow?.flow_type;
    for (const [colId, items] of Object.entries(columnItems)) {
      const dateFiltered = items.filter((it) => !isFutureReport(it, flowType));
      result[colId] = hasActiveFilter ? dateFiltered.filter(matchesFilters) : dateFiltered;
    }
    return result;
  }, [columnItems, hasActiveFilter, matchesFilters, isFutureReport, selectedFlow?.flow_type]);

  const handleClearFilters = () =>
    setFilters({ technical: [], financial: [], projectNumber: '', firm: [], status: [], team: [], tagIds: [] });

  // ── Find which column an item belongs to ─────────────────────────────────

  const findColumnOfItem = useCallback(
    (itemId) => {
      const target = String(itemId);
      for (const [colId, items] of Object.entries(columnItems)) {
        if (items.some((i) => String(i.id) === target)) return colId;
      }
      return null;
    },
    [columnItems]
  );

  // ── DnD handlers ─────────────────────────────────────────────────────────

  const handleDragStart = (event) => {
    const { active } = event;
    for (const items of Object.values(columnItems)) {
      const found = items.find((i) => String(i.id) === String(active.id));
      if (found) {
        setActiveItem(found);
        return;
      }
    }
    // Search in backlog
    const backlogItem = backlogItems.find((i) => String(i.id) === String(active.id));
    if (backlogItem) { setActiveItem(backlogItem); return; }
    // Search in archive
    const archiveItem = archivedItems.find((i) => String(i.id) === String(active.id));
    if (archiveItem) setActiveItem(archiveItem);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveItem(null);

    if (!over || viewingArchived) return;

    // Determine target column
    let targetColId = null;
    const overId = String(over.id);
    if (overId.startsWith('column-')) {
      targetColId = overId.replace('column-', '');
    } else if (columnItems[over.id] !== undefined) {
      targetColId = over.id;
    } else {
      targetColId = findColumnOfItem(over.id);
    }

    // Don't allow dropping back into backlog
    if (!targetColId || targetColId === 'backlog') return;

    const isFromBacklog = String(active.id).startsWith('backlog-');
    const isFromArchive = archivedItems.some((i) => String(i.id) === String(active.id));

    if (isFromBacklog) {
      // ── Backlog → Column drop ──────────────────────────────────────
      if (targetColId === 'archive') return; // Can't drag backlog to archive
      const backlogItem = backlogItems.find((i) => String(i.id) === String(active.id));
      if (!backlogItem) return;

      try {
        const newItem = await addItemToFlow(
          flowDetail.id,
          parseInt(targetColId, 10),
          backlogItem.job_id,
          backlogItem.report_id
        );
        if (newItem) {
          setColumnItems((prev) => ({
            ...prev,
            [targetColId]: [
              ...(prev[targetColId] || []),
              { ...newItem, job: backlogItem.job },
            ],
          }));
        }
      } catch (_err) {
        // error handled by hook
      }
      return;
    }

    if (isFromArchive) {
      // ── Archive → Column drop (unarchive) ──────────────────────────
      if (targetColId === 'archive') return;
      const item = archivedItems.find((i) => String(i.id) === String(active.id));
      if (!item) return;

      // Optimistic: remove from archive, add to column
      setArchivedItems((prev) => prev.filter((i) => String(i.id) !== String(active.id)));
      setColumnItems((prev) => ({
        ...prev,
        [targetColId]: [...(prev[targetColId] || []), { ...item, is_archived: false }],
      }));

      try {
        await unarchiveItem(item.id, parseInt(targetColId, 10));
      } catch (_err) {
        // Revert
        setArchivedItems((prev) => [...prev, item]);
        setColumnItems((prev) => ({
          ...prev,
          [targetColId]: prev[targetColId].filter((i) => i.id !== item.id),
        }));
      }
      return;
    }

    // ── Column → Archive drop ────────────────────────────────────────
    if (targetColId === 'archive') {
      const sourceColId = findColumnOfItem(active.id);
      if (!sourceColId) return;
      const item = columnItems[sourceColId]?.find((i) => String(i.id) === String(active.id));
      if (!item) return;

      // Optimistic
      setColumnItems((prev) => ({
        ...prev,
        [sourceColId]: prev[sourceColId].filter((i) => String(i.id) !== String(active.id)),
      }));
      setArchivedItems((prev) => [...prev, { ...item, is_archived: true }]);

      try {
        await archiveItem(item.id);
      } catch (_err) {
        // Revert
        setColumnItems((prev) => ({
          ...prev,
          [sourceColId]: [...(prev[sourceColId] || []), item],
        }));
        setArchivedItems((prev) => prev.filter((i) => i.id !== item.id));
      }
      return;
    }

    // ── Column → Column move ──────────────────────────────────────────
    const sourceColId = findColumnOfItem(active.id);
    if (!sourceColId || String(sourceColId) === String(targetColId)) return;

    const item = columnItems[sourceColId]?.find((i) => String(i.id) === String(active.id));
    if (!item) return;

    const newOrder = (columnItems[targetColId] || []).length;

    // Look up source/target column metadata so moveItem can run any
    // automatic action attached to the columns (e.g. SET_REPORT_COMPLETED).
    // We also detect whether we're crossing an action boundary so the board
    // can be refreshed afterwards (report status lives in jobs JSONB and
    // isn't reactive on its own).
    const sourceColumn = sortedColumns.find((c) => String(c.id) === String(sourceColId));
    const targetColumn = sortedColumns.find((c) => String(c.id) === String(targetColId));
    const resolveAction = (c) =>
      c?.column_action || (c?.is_completion_column ? 'SET_REPORT_COMPLETED' : null);
    const crossesCompletionBoundary =
      !!sourceColumn && !!targetColumn &&
      resolveAction(sourceColumn) !== resolveAction(targetColumn) &&
      (resolveAction(sourceColumn) === 'SET_REPORT_COMPLETED' ||
       resolveAction(targetColumn) === 'SET_REPORT_COMPLETED');

    setColumnItems((prev) => {
      const srcItems = prev[sourceColId].filter((i) => String(i.id) !== String(active.id));
      const dstItems = [
        ...prev[targetColId],
        { ...item, column_id: targetColId, item_order: newOrder },
      ];
      return { ...prev, [sourceColId]: srcItems, [targetColId]: dstItems };
    });

    try {
      await moveItem(parseInt(active.id, 10), parseInt(targetColId, 10), newOrder, {
        sourceColumn,
        targetColumn,
        jobId: item.job_id,
        reportId: item.report_id,
      });
      if (crossesCompletionBoundary && item.report_id) {
        loadFlowDetail(selectedFlowId);
        if (onReportsChanged) onReportsChanged();
      }
    } catch (_err) {
      setColumnItems((prev) => {
        const srcItems = [...(prev[sourceColId] || []), item];
        const dstItems = prev[targetColId].filter((i) => String(i.id) !== String(active.id));
        return { ...prev, [sourceColId]: srcItems, [targetColId]: dstItems };
      });
    }
  };

  // ── Item actions ──────────────────────────────────────────────────────────

  const handleCompleteItem = (item) => setCompleteDialogItem(item);

  const handleCompleteConfirm = async (itemId, completionDate, submissionDate) => {
    try {
      await completeItem(itemId, completionDate, submissionDate);
      setCompleteDialogItem(null);
      setColumnItems((prev) => {
        const updated = {};
        for (const [colId, items] of Object.entries(prev)) {
          updated[colId] = items.map((i) =>
            i.id === itemId
              ? { ...i, completion_date: completionDate, submission_date: submissionDate }
              : i
          );
        }
        return updated;
      });
    } catch (_err) {
      // error handled by hook
    }
  };

  const handleArchiveItem = async (item) => {
    try {
      await archiveItem(item.id);
      setColumnItems((prev) => {
        const updated = {};
        for (const [colId, items] of Object.entries(prev)) {
          updated[colId] = items.filter((i) => i.id !== item.id);
        }
        return updated;
      });
      setArchivedItems((prev) => [...prev, { ...item, is_archived: true }]);
    } catch (_err) {
      // error handled by hook
    }
  };

  // Arşivden geri al (item-level). Hedef sütun: item'in `column_id`'si
  // hala geçerliyse oraya, degilse siralamada ilk sütun (fallback).
  const handleUnarchiveItem = async (item) => {
    try {
      const preferredCol = sortedColumns.find(
        (c) => String(c.id) === String(item?.column_id),
      );
      const targetColId = preferredCol
        ? preferredCol.id
        : (sortedColumns[0]?.id ?? null);
      if (!targetColId) return;
      await unarchiveItem(item.id, parseInt(targetColId, 10));
      setArchivedItems((prev) => prev.filter((i) => i.id !== item.id));
      setColumnItems((prev) => ({
        ...prev,
        [targetColId]: [
          ...(prev[targetColId] || []),
          { ...item, is_archived: false, column_id: targetColId },
        ],
      }));
    } catch (_err) {
      // error handled by hook
    }
  };


  // ── Active item column color for DragOverlay ────────────────────────────

  const activeItemColumnColor = useMemo(() => {
    if (!activeItem) return '#94a3b8';
    if (String(activeItem.id).startsWith('backlog-')) return '#64748b';
    if (archivedItems.some((i) => i.id === activeItem.id)) return '#78716c';
    const colId = findColumnOfItem(activeItem.id);
    const col = sortedColumns.find((c) => String(c.id) === String(colId));
    return col?.column_color || '#94a3b8';
  }, [activeItem, findColumnOfItem, sortedColumns]);

  // ─────────────────────────────────────────────────────────────────────────

  // Helper -- format archive metadata for archived flow rows
  const formatArchivedAt = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch (_e) {
      return iso;
    }
  };

  const visibleFlows = flowTab === 0 ? filteredActiveFlows : filteredArchivedFlows;
  const showArchivedTab = isRoot;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* ── Top bar: tabs + search + new flow button ────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Tabs
          value={flowTab}
          onChange={handleFlowTabChange}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              minHeight: 36,
              py: 0.5,
            },
          }}
        >
          <Tab
            label={`${t('reportManagement.flows.activeTab', { defaultValue: 'Aktif Akışlar' })} (${activeFlows.length})`}
          />
          {showArchivedTab && (
            <Tab
              label={`${t('reportManagement.flows.archivedTab', { defaultValue: 'Arşivlenmiş Akışlar' })} (${archivedFlows.length})`}
            />
          )}
        </Tabs>

        <Box sx={{ flex: 1 }} />

        <Box sx={{ width: 220, mr: 10 }}>
          <SearchBar
            value={flowSearch}
            onChange={setFlowSearch}
            placeholder={t('reportManagement.flows.searchPlaceholder', { defaultValue: 'Akış ara...' })}
            fullWidth
          />
        </Box>

        {isRoot && flowTab === 0 && (
          <MyButton
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={onCreateFlow}
          >
            {t('reports.flows.newFlow', { defaultValue: 'Yeni Akış' })}
          </MyButton>
        )}
      </Box>

      {/* ── Active flows: scrollable tab/chip row ───────────────────────── */}
      {flowTab === 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mb: 2.5,
            pb: 0.75,
            overflowX: 'auto',
            overflowY: 'hidden',
            flexWrap: 'nowrap',
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: '#cbd5e1',
              borderRadius: 3,
              '&:hover': { bgcolor: '#94a3b8' },
            },
            '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          }}
        >
          {visibleFlows.length === 0 && (
            <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8', py: 1, px: 1 }}>
              {t('reportManagement.flows.noActive', { defaultValue: 'Aktif akış yok' })}
            </Typography>
          )}
          {visibleFlows.map((flow) => {
            const isSelected = String(flow.id) === String(selectedFlowId);
            // For the selected flow we have full local state (columnItems +
            // backlogItems) so we can show what's actually visible — filter-
            // aware AND backlog-inclusive. For other flows we fall back to
            // the server-side count from the flows query.
            const itemCount = isSelected
              ? Object.values(filteredColumnItems).reduce(
                  (acc, items) => acc + items.length,
                  0,
                ) +
                (flow.source_type === 'REPORT_PLAN'
                  ? (() => {
                      const visible = (backlogItems || []).filter((it) => !isFutureReport(it, flow.flow_type));
                      return (hasActiveFilter ? visible.filter(matchesFilters) : visible).length;
                    })()
                  : 0)
              : Array.isArray(flow.report_flow_items)
                // Seçilmemiş akışlarda da gelecek tarihli raporları sayma —
                // tab badge'i ekrana yansıyan kart sayısıyla aynı olsun.
                ? flow.report_flow_items.filter((it) => !isFutureReport(it, flow.flow_type)).length
                : 0;
            return (
              <Box
                key={flow.id}
                onClick={() => onFlowSelect && onFlowSelect(flow.id)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  height: 34,
                  px: 1.5,
                  borderRadius: '10px',
                  cursor: 'pointer',
                  flexShrink: 0,
                  fontSize: '0.82rem',
                  fontWeight: isSelected ? 700 : 500,
                  color: isSelected ? '#fff' : '#475569',
                  bgcolor: isSelected ? '#3b82f6' : '#f1f5f9',
                  border: '1px solid',
                  borderColor: isSelected ? '#3b82f6' : 'transparent',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: isSelected ? '#2563eb' : '#e2e8f0',
                  },
                }}
              >
                <span>{flow.flow_title || `Akış #${flow.id}`}</span>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 22,
                    height: 18,
                    borderRadius: '999px',
                    bgcolor: isSelected ? 'rgba(255,255,255,0.25)' : '#fff',
                    color: isSelected ? '#fff' : '#64748b',
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    px: 0.6,
                  }}
                >
                  {itemCount}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* ── Archived flows section (ROOT only) ──────────────────────────── */}
      {showArchivedTab && flowTab === 1 && !viewingArchived && (
        <Box sx={{ mt: 1 }}>
          {filteredArchivedFlows.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 8,
                gap: 1,
                color: '#94a3b8',
              }}
            >
              <ArchiveOutlinedIcon sx={{ fontSize: '2rem' }} />
              <Typography sx={{ fontSize: '0.9rem' }}>
                {t('reportManagement.flows.noArchived', { defaultValue: 'Arşivlenmiş akış yok' })}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {filteredArchivedFlows.map((flow) => {
                const archiver = flow.archived_by_user;
                const archiverName = archiver
                  ? `${archiver.first_name || ''} ${archiver.last_name || ''}`.trim()
                  : '-';
                return (
                  <Box
                    key={flow.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1.75,
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      bgcolor: '#fff',
                      transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
                      '&:hover': {
                        borderColor: '#cbd5e1',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                      },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.95rem',
                          color: '#0f172a',
                          mb: 0.25,
                        }}
                      >
                        {flow.flow_title || `Akış #${flow.id}`}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                          <strong style={{ color: '#64748b' }}>
                            {t('reportManagement.flows.archivedAt', { defaultValue: 'Arşivleme tarihi' })}:
                          </strong>{' '}
                          {formatArchivedAt(flow.archived_at)}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                          <strong style={{ color: '#64748b' }}>
                            {t('reportManagement.flows.archivedBy', { defaultValue: 'Arşivleyen' })}:
                          </strong>{' '}
                          {archiverName}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityOutlinedIcon sx={{ fontSize: '1rem' }} />}
                      onClick={() => { setViewingArchived(true); if (onFlowSelect) onFlowSelect(flow.id); }}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        borderColor: '#cbd5e1',
                        color: '#475569',
                        '&:hover': { borderColor: '#3b82f6', color: '#3b82f6', bgcolor: '#eff6ff' },
                      }}
                    >
                      {t('reportManagement.flows.viewAction', { defaultValue: 'Görüntüle' })}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<UnarchiveOutlinedIcon sx={{ fontSize: '1rem' }} />}
                      onClick={() => setRestoreConfirmFlow(flow)}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        borderColor: '#cbd5e1',
                        color: '#475569',
                        '&:hover': { borderColor: '#3b82f6', color: '#3b82f6', bgcolor: '#eff6ff' },
                      }}
                    >
                      {t('reportManagement.flows.restoreAction', { defaultValue: 'Yeniden Aç' })}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteOutlineIcon sx={{ fontSize: '1rem' }} />}
                      onClick={() => setDeleteConfirmFlow(flow)}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                      }}
                    >
                      {t('reportManagement.flows.deletePermanently', { defaultValue: 'Kalıcı Olarak Sil' })}
                    </Button>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      )}

      {/* ── Empty state for active tab when nothing is selected ─────────── */}
      {flowTab === 0 && !selectedFlowId && !viewingArchived && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 10,
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '20px',
              bgcolor: '#f1f5f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1,
            }}
          >
            <ViewKanbanIcon sx={{ fontSize: '2.5rem', color: '#94a3b8' }} />
          </Box>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 600, color: '#475569' }}>
            {t('reports.flows.emptyState', {
              defaultValue: 'Bir akış seçin veya yeni akış oluşturun',
            })}
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8', maxWidth: 360, textAlign: 'center' }}>
            {t('reports.flows.emptyStateHint', {
              defaultValue: 'Rapor akışlarınızı kanban görünümünde yönetin',
            })}
          </Typography>
        </Box>
      )}

      {/* ── Selected flow board ─────────────────────────────────────────── */}
      {(flowTab === 0 || viewingArchived) && selectedFlowId && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: 2.5,
              pb: 2,
              borderBottom: '1px solid #f1f5f9',
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {flowDetail && (
                <>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: '1.1rem',
                      color: '#0f172a',
                      lineHeight: 1.3,
                    }}
                  >
                    {flowDetail.flow_title}
                  </Typography>
                  {flowDetail.flow_description && (
                    <Typography
                      sx={{
                        fontSize: '0.8rem',
                        color: '#94a3b8',
                        mt: 0.25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {flowDetail.flow_description}
                    </Typography>
                  )}
                </>
              )}
            </Box>

            {viewingArchived && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => { setViewingArchived(false); if (onFlowSelect) onFlowSelect(''); }}
                sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}
              >
                {t('reportManagement.flows.backToArchived', { defaultValue: 'Arşivlenmiş Akışlara Dön' })}
              </Button>
            )}

            {!viewingArchived && (canEdit || isRoot) && flowDetail && (
              <>
                <Tooltip title={t('reportManagement.tagManager.title', { defaultValue: 'Etiketleri Yönet' })}>
                  <IconButton
                    size="small"
                    onClick={() => setTagManagerOpen(true)}
                    sx={{
                      p: 0.75,
                      color: flowTags.length > 0 ? '#3b82f6' : '#94a3b8',
                      border: '1px solid',
                      borderColor: flowTags.length > 0 ? '#bfdbfe' : '#e2e8f0',
                      borderRadius: '8px',
                      '&:hover': { bgcolor: '#f8fafc', color: '#2563eb' },
                    }}
                  >
                    <LocalOfferOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('common.edit')}>
                  <IconButton
                    size="small"
                    onClick={() => setEditFlowOpen(true)}
                    sx={{
                      p: 0.75,
                      color: '#94a3b8',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      '&:hover': { bgcolor: '#f8fafc', color: '#475569' },
                    }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {isRoot && !viewingArchived && flowDetail && (
              <Tooltip title={t('common.more', { defaultValue: 'Daha fazla' })}>
                <IconButton
                  size="small"
                  onClick={handleOpenFlowMenu}
                  sx={{
                    p: 0.75,
                    color: '#94a3b8',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    '&:hover': { bgcolor: '#f8fafc', color: '#475569' },
                  }}
                >
                  <MoreVertIcon sx={{ fontSize: '1.1rem' }} />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title={t('reports.board.refresh', { defaultValue: 'Yenile' })}>
              <IconButton
                size="small"
                onClick={() => {
                  if (onRefresh) onRefresh();
                  loadFlowDetail(selectedFlowId);
                  refetchBacklog();
                }}
                sx={{
                  p: 0.75,
                  color: '#94a3b8',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  '&:hover': { bgcolor: '#f8fafc', color: '#475569' },
                }}
              >
                <RefreshIcon sx={{ fontSize: '1.1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Flow action menu (ROOT only) */}
          <Menu
            anchorEl={flowMenuAnchor}
            open={Boolean(flowMenuAnchor)}
            onClose={handleCloseFlowMenu}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleArchiveClick}>
              <ListItemIcon>
                {selectedFlow?.flow_type === 'CONTINUOUS' ? (
                  <StopCircleOutlinedIcon fontSize="small" />
                ) : (
                  <ArchiveOutlinedIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  selectedFlow?.flow_type === 'CONTINUOUS'
                    ? t('reportManagement.flows.endAndArchiveAction', { defaultValue: 'Sonlandır ve Arşive Ekle' })
                    : t('reportManagement.flows.archiveAction', { defaultValue: 'Akışı Arşive Taşı' })
                }
                primaryTypographyProps={{ fontSize: '0.85rem' }}
              />
            </MenuItem>
          </Menu>

          {/* ── Board filter toolbar (client-side filters) ────────────────── */}
          {!detailLoading && flowDetail && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                flexWrap: 'wrap',
              }}
            >
              {/* Firma — free-text giriş için Autocomplete (freeSolo). Liste-dışı
                  arama metinleri de filtreye eklenir; eşleşme substring/case-
                  insensitive olarak matchesFilters'da yapılır. */}
              <Autocomplete
                multiple
                freeSolo
                size="small"
                options={firmOptions}
                value={filters.firm}
                onChange={(_e, newValue) =>
                  setFilters((prev) => ({ ...prev, firm: newValue }))
                }
                sx={{ minWidth: 220, maxWidth: 320 }}
                renderTags={(value, getTagProps) =>
                  value.map((val, index) => {
                    const { key, ...rest } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        {...rest}
                        label={val}
                        size="small"
                        sx={{ height: 20, fontSize: '0.72rem' }}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('reportManagement.boardFilters.firm', { defaultValue: 'Firma' })}
                    placeholder={t('reportManagement.boardFilters.firmPlaceholder', { defaultValue: 'Yaz veya seç…' })}
                  />
                )}
              />

              {/* Proje No */}
              <TextField
                size="small"
                value={filters.projectNumber}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, projectNumber: e.target.value }))
                }
                label={t('reportManagement.boardFilters.projectNumber', { defaultValue: 'Proje No' })}
                placeholder={t('reportManagement.boardFilters.placeholder.projectNumber', { defaultValue: 'Proje no...' })}
                sx={{ minWidth: 180, maxWidth: 220 }}
              />
              
              {/* Teknik Sorumlu */}
              <FormControl size="small" sx={{ minWidth: 200, maxWidth: 260 }}>
                <InputLabel>
                  {t('reportManagement.boardFilters.technical', { defaultValue: 'Teknik Sorumlu' })}
                </InputLabel>
                <Select
                  multiple
                  value={filters.technical}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, technical: e.target.value }))
                  }
                  input={
                    <OutlinedInput
                      label={t('reportManagement.boardFilters.technical', { defaultValue: 'Teknik Sorumlu' })}
                    />
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => {
                        const opt = technicalOptions.find((o) => o.id === id);
                        return (
                          <Chip
                            onClick={() => {}}
                            key={id}
                            label={opt?.name || `#${id}`}
                            size="small"
                            sx={{ height: 20, fontSize: '0.72rem' }}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {technicalOptions.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Mali Sorumlu */}
              <FormControl size="small" sx={{ minWidth: 200, maxWidth: 260 }}>
                <InputLabel>
                  {t('reportManagement.boardFilters.financial', { defaultValue: 'Mali Sorumlu' })}
                </InputLabel>
                <Select
                  multiple
                  value={filters.financial}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, financial: e.target.value }))
                  }
                  input={
                    <OutlinedInput
                      label={t('reportManagement.boardFilters.financial', { defaultValue: 'Mali Sorumlu' })}
                    />
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => {
                        const opt = financialOptions.find((o) => o.id === id);
                        return (
                          <Chip
                            onClick={() => {}}
                            key={id}
                            label={opt?.name || `#${id}`}
                            size="small"
                            sx={{ height: 20, fontSize: '0.72rem' }}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {financialOptions.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Durum */}
              <FormControl size="small" sx={{ minWidth: 180, maxWidth: 240 }}>
                <InputLabel>
                  {t('reportManagement.filters.status', { defaultValue: 'Durum' })}
                </InputLabel>
                <Select
                  multiple
                  value={filters.status}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, status: e.target.value }))
                  }
                  input={
                    <OutlinedInput
                      label={t('reportManagement.filters.status', { defaultValue: 'Durum' })}
                    />
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((s) => (
                        <Chip
                          onClick={() => {}}
                          key={s}
                          label={t(`reportManagement.statuses.${s}`, { defaultValue: s })}
                          size="small"
                          sx={{ height: 20, fontSize: '0.72rem' }}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {STATUS_FILTER_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {t(`reportManagement.statuses.${s}`, { defaultValue: s })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Ekip */}
              <FormControl size="small" sx={{ minWidth: 180, maxWidth: 240 }}>
                <InputLabel>
                  {t('reportManagement.filters.team', { defaultValue: 'Ekip' })}
                </InputLabel>
                <Select
                  multiple
                  value={filters.team}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, team: e.target.value }))
                  }
                  input={
                    <OutlinedInput
                      label={t('reportManagement.filters.team', { defaultValue: 'Ekip' })}
                    />
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => {
                        const opt = teamOptions.find((o) => o.id === id);
                        return (
                          <Chip
                            onClick={() => {}}
                            key={id}
                            label={opt?.name || `#${id}`}
                            size="small"
                            sx={{ height: 20, fontSize: '0.72rem' }}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {teamOptions.map((tm) => (
                    <MenuItem key={tm.id} value={tm.id}>
                      {tm.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Akis-ozel etiket filtresi. Renkli chip + checkbox seçici. */}
              {flowTags.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 200, maxWidth: 260 }}>
                  <InputLabel>
                    {t('reportManagement.boardFilters.tags', { defaultValue: 'Etiketler' })}
                  </InputLabel>
                  <Select
                    multiple
                    value={filters.tagIds}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, tagIds: e.target.value }))
                    }
                    input={
                      <OutlinedInput
                        label={t('reportManagement.boardFilters.tags', { defaultValue: 'Etiketler' })}
                      />
                    }
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((tagId) => {
                          const tag = flowTags.find((t) => t.id === tagId);
                          if (!tag) return null;
                          return (
                            <Chip
                              onClick={() => {}}
                              key={tagId}
                              label={tag.name}
                              size="small"
                              sx={{
                                height: 20, fontSize: '0.72rem',
                                bgcolor: tag.color, color: '#fff', fontWeight: 600,
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {flowTags.map((tag) => (
                      <MenuItem key={tag.id} value={tag.id}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: tag.color, mr: 1 }} />
                        {tag.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Box sx={{ flex: 1 }} />

              {hasActiveFilter && (
                <Button
                  size="small"
                  onClick={handleClearFilters}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    color: '#475569',
                  }}
                >
                  {t('reportManagement.boardFilters.clear', { defaultValue: 'Filtreleri Temizle' })}
                </Button>
              )}
            </Box>
          )}

          {/* Loading */}
          {detailLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
              <CircularProgress size={32} sx={{ color: '#94a3b8' }} />
            </Box>
          )}

      {/* Board */}
      {!detailLoading && flowDetail && (
        <DndContext
          sensors={sensors}
          collisionDetection={(args) => {
            const pointerHits = pointerWithin(args);
            return pointerHits.length > 0 ? pointerHits : rectIntersection(args);
          }}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              overflowX: 'auto',
              overflowY: 'hidden',
              pb: 2,
              // Sayfa yerine board'un kendi yüksekliğini sabitle ki kolonlar
              // kendi içinde scroll edebilsin. Header/filter yüksekliği için
              // calc() ile pay bırakıyoruz.
              height: 'calc(100vh - 280px)',
              minHeight: 400,
              // Scrollbar
              '&::-webkit-scrollbar': { height: 6 },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: '#cbd5e1',
                borderRadius: 3,
                '&:hover': { bgcolor: '#94a3b8' },
              },
              '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
            }}
          >
            {/* Backlog column — only meaningful for REPORT_PLAN flows.
                JOB_LIST flows fill themselves with every matching job, so a
                "pending reports" backlog is conceptually empty/misleading. */}
            {flowDetail?.source_type === 'REPORT_PLAN' && (
              <BacklogColumn
                items={(() => {
                  const dateFiltered = (backlogItems || []).filter((it) => !isFutureReport(it, flowDetail?.flow_type));
                  return hasActiveFilter ? dateFiltered.filter(matchesFilters) : dateFiltered;
                })()}
                loading={backlogLoading}
              />
            )}

            {sortedColumns.map((column) => (
              <KanbanColumn
                key={column.id}
                column={column}
                items={filteredColumnItems[column.id] || []}
                onComplete={handleCompleteItem}
                onArchive={handleArchiveItem}
                onCardClick={setDetailItem}
                isRoot={isRoot && !viewingArchived}
                canEdit={canEdit && !viewingArchived}
                itemTagMap={itemTagMap}
                reportTypeMap={reportTypeMap}
              />
            ))}

            {/* Archive column */}
            <ArchiveColumn
              items={archivedItems}
              loading={archiveLoading}
              onUnarchive={viewingArchived ? undefined : handleUnarchiveItem}
            />

            {/* Empty columns state */}
            {sortedColumns.length === 0 && (
              <Box
                sx={{
                  p: 5,
                  textAlign: 'center',
                  color: '#94a3b8',
                  border: '2px dashed #e2e8f0',
                  borderRadius: '12px',
                  minWidth: 320,
                  bgcolor: '#fafbfc',
                }}
              >
                <InboxIcon sx={{ fontSize: '2rem', color: '#cbd5e1', mb: 1 }} />
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                  {t('reports.board.noColumns', { defaultValue: 'Bu akista henuz sutun yok.' })}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Drag overlay -- ghost card with rotation */}
          <DragOverlay>
            {activeItem ? (
              <Box
                sx={{
                  opacity: 0.92,
                  pointerEvents: 'none',
                  transform: 'rotate(2deg)',
                  filter: 'drop-shadow(0 12px 24px rgba(0,0,0,0.18))',
                  width: 304,
                }}
              >
                <KanbanCard
                  item={activeItem}
                  columnColor={activeItemColumnColor}
                  isRoot={false}
                  isOverlay
                  reportTypeMap={reportTypeMap}
                />
              </Box>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
        </>
      )}


      {/* Complete item dialog */}
      {completeDialogItem && (
        <CompleteItemDialog
          open={Boolean(completeDialogItem)}
          item={completeDialogItem}
          onClose={() => setCompleteDialogItem(null)}
          onConfirm={handleCompleteConfirm}
        />
      )}

      {/* Card detail modal */}
      <CardDetailModal
        open={!!detailItem}
        item={detailItem}
        onClose={() => setDetailItem(null)}
        onCommentChange={() => { /* no-op for now; comments are owned by modal */ }}
        flowTags={flowTags}
        itemTags={detailItem ? (itemTagMap?.[detailItem.id] || []) : []}
        reportTypeMap={reportTypeMap}
        onSaveTags={async (tagIds) => {
          if (!detailItem) return;
          await setItemFlowTags(detailItem.id, tagIds);
        }}
      />

      {/* Etiket yonetici — akisa ozel */}
      <FlowTagManagerDialog
        open={tagManagerOpen}
        onClose={() => setTagManagerOpen(false)}
        tags={flowTags}
        onCreate={async ({ name, color }) => {
          try { await createFlowTag({ name, color }); }
          catch (e) {
            showNotification(
              t('reportManagement.tagManager.createError', { defaultValue: 'Etiket eklenemedi (aynı isim olabilir).' }),
              'error',
            );
          }
        }}
        onUpdate={async ({ id, name, color }) => {
          try { await updateFlowTag({ id, name, color }); }
          catch (e) {
            showNotification(
              t('reportManagement.tagManager.updateError', { defaultValue: 'Etiket güncellenemedi.' }),
              'error',
            );
          }
        }}
        onDelete={async (id) => {
          try { await deleteFlowTag(id); }
          catch (e) {
            showNotification(
              t('reportManagement.tagManager.deleteError', { defaultValue: 'Etiket silinemedi.' }),
              'error',
            );
          }
        }}
      />

      {/* Edit flow dialog */}
      {editFlowOpen && flowDetail && (
        <EditFlowDialog
          open={editFlowOpen}
          onClose={() => setEditFlowOpen(false)}
          flow={flowDetail}
          onSuccess={(result) => {
            setEditFlowOpen(false);
            if (result === 'deleted') {
              // Flow silindi — listeyi yenile ve seçimi temizle
              if (onRefresh) onRefresh();
              if (onFlowSelect) onFlowSelect('');
            } else {
              // Flow güncellendi — detayı ve listeyi yenile
              if (onRefresh) onRefresh();
              loadFlowDetail(selectedFlowId);
              refetchBacklog();
            }
          }}
        />
      )}

      {/* ── Archive confirmation dialog (active flow → archive) ──────── */}
      <Dialog
        open={archiveConfirmOpen}
        onClose={() => !actionInFlight && setArchiveConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {selectedFlow?.flow_type === 'CONTINUOUS'
            ? t('reportManagement.flows.endAndArchiveAction', { defaultValue: 'Sonlandır ve Arşive Ekle' })
            : t('reportManagement.flows.archiveAction', { defaultValue: 'Akışı Arşive Taşı' })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('reportManagement.flows.archiveConfirm', {
              defaultValue: 'Bu akışı arşivlemek istediğinize emin misiniz?',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveConfirmOpen(false)} disabled={actionInFlight} color="inherit">
            {t('common.cancel', { defaultValue: 'İptal' })}
          </Button>
          <Button
            onClick={handleConfirmArchive}
            disabled={actionInFlight}
            variant="contained"
            color="warning"
          >
            {t('common.confirm', { defaultValue: 'Onayla' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Restore confirmation dialog (archived flow → active) ─────── */}
      <Dialog
        open={Boolean(restoreConfirmFlow)}
        onClose={() => !actionInFlight && setRestoreConfirmFlow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t('reportManagement.flows.restoreAction', { defaultValue: 'Yeniden Aç' })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('reportManagement.flows.restoreConfirm', {
              defaultValue: 'Akışı yeniden açmak istediğinize emin misiniz?',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRestoreConfirmFlow(null)}
            disabled={actionInFlight}
            color="inherit"
          >
            {t('common.cancel', { defaultValue: 'İptal' })}
          </Button>
          <Button
            onClick={handleConfirmRestore}
            disabled={actionInFlight}
            variant="contained"
            color="primary"
          >
            {t('common.confirm', { defaultValue: 'Onayla' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Hard-delete confirmation dialog (archived flow) ──────────── */}
      <Dialog
        open={Boolean(deleteConfirmFlow)}
        onClose={() => !actionInFlight && setDeleteConfirmFlow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t('reportManagement.flows.deletePermanently', { defaultValue: 'Kalıcı Olarak Sil' })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('reportManagement.flows.deleteConfirm', {
              defaultValue:
                'Bu akışı kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmFlow(null)}
            disabled={actionInFlight}
            color="inherit"
          >
            {t('common.cancel', { defaultValue: 'İptal' })}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={actionInFlight}
            variant="contained"
            color="error"
          >
            {t('common.confirm', { defaultValue: 'Onayla' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportBoardView;
